import {
  BrightyListItem,
  DateTime,
  ListContentIsland,
  Money,
  PricingListItem,
  RelativeDateTime,
  Skeleton,
  Stack,
  Typography,
} from '@razz-team/ui-web';
import { useTranslation } from 'react-i18next';
import { useVaultQuery, useVaultProductQuery } from '../../../api/vaults';
import { useMemo } from 'react';

export function VaultRewards({ vaultId }: { vaultId: string }) {
  const { t } = useTranslation();
  const vaultQuery = useVaultQuery({ vaultId });
  const vaultProductQuery = useVaultProductQuery();

  const vault = vaultQuery.data;
  const conditions = vaultProductQuery.data?.product.conditions;

  const condition = useMemo(
    () => conditions?.find((it) => it.currency === vault?.balance.currency),
    [vault, conditions],
  );

  const apy = useMemo(() => {
    if (!vault?.productId || !condition) return null;

    return condition?.rate ? condition.rate : null;
  }, [vault, condition]);

  const nextPayout = useMemo(() => {
    if (!vaultQuery.isSuccess) return null;
    const nextReward = vaultQuery.data?.nextReward?.date;
    if (!nextReward) return t('No upcoming rewards');
    return <RelativeDateTime value={nextReward} variant="subtitle2" />;
  }, [vaultQuery, t]);

  return (
    <ListContentIsland>
      <PricingListItem
        label={t('Current APY')}
        value={
          apy ? (
            <Stack>
              <Typography variant="subtitle2">{`${apy}%`}</Typography>
              <Typography variant="caption" color="text.secondary">
                {t('Based on DeFi-only staking instruments')}
              </Typography>
            </Stack>
          ) : (
            <Skeleton />
          )
        }
      />
      <PricingListItem label={t('Next payout')} value={nextPayout} />
      <PricingListItem
        label={t('Minimum balance to receive APY')}
        value={
          condition?.minAmount ? (
            <Money value={condition.minAmount} variant="subtitle2" />
          ) : (
            <Skeleton />
          )
        }
      />
      <PricingListItem
        label={t('Vault opened on')}
        value={
          vault?.openedAt ? (
            <DateTime value={vault.openedAt} format="date" variant="subtitle2" />
          ) : (
            <Skeleton />
          )
        }
      />
      <PricingListItem
        label={t('Where earnings go')}
        value={<Typography variant="subtitle2">{t('Always capitalized')}</Typography>}
      />
      <PricingListItem
        label={t('Earnings payout interval')}
        value={<Typography variant="subtitle2">{t('Paid daily')}</Typography>}
      />
      <BrightyListItem<'a'>
        title={t('Terms & conditions for Earning Vaults')}
        href="https://brighty.app/en/earning-vault-terms-and-conditions"
        target="_blank"
      />
    </ListContentIsland>
  );
}
